import { InsightFilter } from '@api/api/insights';
import { getConditionIcons } from '@terminal/components/insight-icons/utils';
import { InsightIconsRoot } from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { InsightDetails } from '@terminal/components/insights-group/insight-card/insights-details/InsightDetails';
import { useInsightDrawer } from '@terminal/hooks/use-insight-drawer/useInsightDrawer';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { HORIZONTAL_TABLE_COMBINE_SIZE } from '@terminal/views/explore/components/truncate-combined-icons/TruncateCombinedIcons';
import {
  TABLE_SINGLE_HEIGHT,
  TABLE_SINGLE_WIDTH,
} from '@terminal/views/explore/components/truncate-single-icons/TruncateSingleIcons';
import { isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from './ClickableScenarioInsightTooltip.module.scss';

interface ClickableScenarioInsightTooltipProps {
  insights: InsightFilter[];
  entityTag: string;
  source: PosthogTrackingSource;
}

export const ClickableScenarioInsightTooltip = ({
  insights,
  entityTag,
  source,
}: ClickableScenarioInsightTooltipProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer } = useInsightDrawer();

  return (
    <div className={styles.scrollableContainer}>
      {insights.map(insight => {
        const {
          median_return: median,
          horizon,
          stars,
          conditions: insightConditions,
        } = insight.metadata;
        const episodeLabel = t('insights:insightCard.episode', {
          count: insight.metadata.episode_count,
        });
        const isCombined = insight.metadata.kind === 'combination';
        const isStarred = isToday(new Date(insight.created_at));
        const direction = getExploreDirection(insight.metadata.direction);
        const customSize = isCombined
          ? HORIZONTAL_TABLE_COMBINE_SIZE
          : {
              width: `${TABLE_SINGLE_WIDTH}px`,
              height: `${TABLE_SINGLE_HEIGHT}px`,
            };
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insightConditions,
        });

        const handleClick = () => {
          openDrawer({
            id: insight.id,
            entityTag: insight.metadata.entity,
            trackingProperties: { source },
          });
        };

        return (
          <div key={insight.id} onClick={handleClick}>
            <div className={styles.tooltipWrapper}>
              <InsightIconsRoot
                conditions={conditions}
                direction={direction}
                isStarred={isStarred}
                customSize={customSize}
                isCombined={isCombined}
                layout={isCombined ? 'horizontal' : 'vertical'}
              />
              <InsightDetails
                isTooltip
                median={median}
                horizon={horizon}
                name={insight.card.name}
                stars={stars}
                direction={direction}
                isNewInsight={isStarred}
                entityTag={entityTag}
                episodeLabel={episodeLabel}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
